/* eslint-disable max-lines */
import {
  type AiRecruiterLearnings,
  type DynamicScreeningQuestion,
  HireType,
  type Job,
  type JobCustomSkill,
  JobCustomSkillField,
  JobLocationContext,
  type JobTaxonomyMachineMapWithRelations,
  type JobTitle,
  JobTravelExpectations,
  type JobWithRelations,
  PayRatePeriod,
  PolicyEnforcement,
  PolicyName,
  type Project,
  type ProjectCopilotConfigurationV1Snapshot,
  ScreeningAction,
  ScreeningType,
  V1SnapshotCommunicationTone,
  V1SnapshotMessagingWindow,
  type V1SnapshotOutreachRadius,
  V1SnapshotPushiness,
  V1SnapshotScoreThreshold,
  V1SnapshotStrictness,
  V1SnapshotThoroughness,
} from '@factoryfixinc/ats-interfaces';

import { JobPersistence } from '@/core/shared/job/job.persistence';
import { useCopilotActivationStore } from './copilot-activation.store';
import { CustomMachinePersistence } from '@/core/shared/custom-machine/custom-machine.persistence';
import type { JobCustomMachine } from '@factoryfixinc/ats-interfaces';
import { CustomSkillPersistence } from '@/core/shared/custom-skill/custom-skill.persistence';
import { DEFAULT_OUTREACH_RADIUS_MILES, isOutreachRadiusOption } from './outreach-radius.constants';
import JobTitleService from '@/core/shared/job-title/job-title.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import TaxonomyService from '@/core/shared/taxonomy/taxonomy.service';
import VersionsService from '@/core/jobs/versions/versions.service';
import TrackingService from '@/core/shared/tracking/tracking.service';

import type {
  GenerateJobDescriptionOptions,
  GenerateScreenerQuestionsOptions,
  GeneratedJobDescriptionDocument,
  GeneratedScreenerQuestionsDocument,
} from '@/core/shared/job/job.type';
import { EmployerFeatureService } from '@/core/shared/employer-feature/employer-feature.service';
import { EmployerService } from '@/core/shared/employer/employer.service';
import {
  type LocalScreenerQuestion,
  MINIMUM_REQUIRED_SCREENER_QUESTIONS,
  type ScreeningTypeWithSkip,
} from './types/local-screener-question.type';
import type { JobUpdateOutcome } from './types/job-update-outcome.type';
import {
  cleanHTMLJobDescription,
  makeMarkdownVersionOfJobDescription,
} from '@/core/jobs/copilot-activation/utils/html-parsing.utils';
import type { LocalJobData } from './local-job-data.type';
import ProjectService from '@/core/shared/project/project.service';
import {
  TrackingActionName,
  TrackingJobDescriptionParsingError,
  TrackingJobDescriptionParsingErrorSource,
} from '@/core/shared/tracking/tracking-actions';
import { JobDescriptionUtils } from '@/utils/job-description-util/job-description.util';
import { ErrorService } from '@/core/shared/errors/error.service';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';
import MeService from '@/core/shared/me/me.service';
import { getUserName } from '@/core/shared/user-profile/utils';
import type { ProjectInterviewSettings } from './types/interview-scheduling-settings.type';
import { isEnumMember } from '@/utils/is-enum-member.util';
import {
  type WorkPerformed,
  isFieldContext,
  locationContextFor,
  workPerformedFor,
  workerLivesFor,
} from './location-context.utils';
import { syncTravelDisclosure, travelDisclosureText } from './travel-disclosure.utils';

export default class CopilotActivationService {
  private readonly jobTitleService = new JobTitleService();
  private readonly jobPersistence = new JobPersistence();
  private readonly customSkillPersistence = new CustomSkillPersistence();
  private readonly customMachinePersistence = new CustomMachinePersistence();
  private tempCustomMachines: string[] = [];
  private loadingProjectId: number | null = null;
  private readonly employerService = new EmployerService();
  private readonly employerFeatureService = new EmployerFeatureService();
  private readonly taxonomyService = new TaxonomyService();
  private readonly copilotActivationStore = useCopilotActivationStore();
  private readonly jobVersionsService = new VersionsService();
  private readonly projectService = new ProjectService();
  private readonly meService = new MeService();

  public get selectedJob(): JobWithRelations<
    | 'jobTitle'
    | 'screenerQuestions'
    | 'jobQualifications'
    | 'jobResponsibilities'
    | 'jobTaxonomyKnowledgeDisciplineMaps'
    | 'jobTaxonomyMachineMaps'
    | 'jobStatusHistories'
  > | null {
    return this.copilotActivationStore.selectedJob;
  }

  public get selectedProjectId(): number | null {
    return this.copilotActivationStore.selectedProjectId;
  }

  public get selectedJobId(): number | null {
    return this.copilotActivationStore.selectedJobId;
  }

  // ====== START: DATA MAPPING ======
  public get originalJobTitle(): string | null {
    return this.copilotActivationStore.selectedJob?.displayTitle ?? null;
  }

  public get originalHireType(): HireType | null {
    return this.copilotActivationStore.selectedJob?.hireType ?? null;
  }

  public get originalShift(): number | null {
    return this.copilotActivationStore.selectedJob?.shift ?? null;
  }

  public get jobStatus(): Job['status'] | null {
    return this.copilotActivationStore.jobStatus;
  }

  public set jobStatus(value: Job['status'] | null) {
    this.copilotActivationStore.jobStatus = value;
  }

  public get originalScreeningType(): string | null {
    return this.copilotActivationStore.selectedJob?.screeningType ?? ScreeningType.STATIC;
  }

  // Validations
  public get hasTriedToUpdateJob(): boolean {
    return this.copilotActivationStore.hasTriedToUpdateJob;
  }

  public set hasTriedToUpdateJob(value: boolean) {
    this.copilotActivationStore.hasTriedToUpdateJob = value;
  }

  public get localJobData(): LocalJobData {
    return this.copilotActivationStore.localJobData;
  }

  public get localJobDataInformation(): { [key: string]: string } {
    return {
      jobData: JSON.stringify(this.copilotActivationStore.localJobData),
      knowledgeDisciplines: JSON.stringify(this.knowledgeDisciplines),
      machines: JSON.stringify(this.machines),
      customKnowledgeDisciplines: JSON.stringify(this.customKnowledgeDisciplines),
      customMachines: JSON.stringify(this.customMachines),
      brandsPerMachine: JSON.stringify(this.orderedBrandsPerMachine),
      screenerQuestions: JSON.stringify(this.screenerQuestions),
      screeningAction: JSON.stringify(this.screeningAction),
      screeningType: JSON.stringify(this.screeningType),
    };
  }

  // DISPLAY TITLE
  public get displayTitle(): string | null {
    if (this.copilotActivationStore.localJobData.displayTitle) {
      return this.copilotActivationStore.localJobData.displayTitle.trim().toLocaleLowerCase() ===
        'no title'
        ? ''
        : this.copilotActivationStore.localJobData.displayTitle;
    } else {
      return '';
    }
  }
  public set displayTitle(value: string | null) {
    this.copilotActivationStore.localJobData.displayTitle = value;
  }

  // JOB TITLE
  public get jobTitleId(): number | null {
    return this.copilotActivationStore.localJobData.jobTitleId;
  }

  public set jobTitleId(value: number | null) {
    this.copilotActivationStore.localJobData.jobTitleId = value;
  }

  // HIRE TYPE
  public get hireType(): HireType | null {
    return this.copilotActivationStore.localJobData.hireType;
  }
  public set hireType(value: HireType | null) {
    const oldHireTypeValue = this.copilotActivationStore.localJobData.hireType;
    this.copilotActivationStore.localJobData.hireType = value;

    if (value === HireType.PROJECT) {
      // Set shift = 4 as that portrays the PROJECT hireType
      this.copilotActivationStore.localJobData.shift = 4;
      // startTime and endTime should be null for PROJECT hireType
      this.copilotActivationStore.localJobData.startTime = null;
      this.copilotActivationStore.localJobData.endTime = null;
    } else if (oldHireTypeValue === HireType.PROJECT) {
      // Reset shift to null, do not show the user the PROJECT shift
      this.copilotActivationStore.localJobData.shift = null;
    }
  }

  // Shift
  public get shift(): number | null {
    return this.copilotActivationStore.localJobData.shift;
  }
  public set shift(value: number | null) {
    this.copilotActivationStore.localJobData.shift = value;

    // For all the shifts other than 0 (Other), set startTime and endTime to null
    if (value !== 0) {
      this.copilotActivationStore.localJobData.startTime = null;
      this.copilotActivationStore.localJobData.endTime = null;
    }
  }

  // Shift start/end time
  public get startTime(): string | null {
    return this.copilotActivationStore.localJobData.startTime;
  }
  public set startTime(value: string | null) {
    this.copilotActivationStore.localJobData.startTime = value;
  }
  public get endTime(): string | null {
    return this.copilotActivationStore.localJobData.endTime;
  }
  public set endTime(value: string | null) {
    this.copilotActivationStore.localJobData.endTime = value;
  }

  // Pay rate min/max
  public get payRateMin(): number | null {
    return this.copilotActivationStore.localJobData.payRateMin;
  }
  public set payRateMin(value: number | null) {
    this.copilotActivationStore.localJobData.payRateMin = value;
  }
  public get payRateMax(): number | null {
    return this.copilotActivationStore.localJobData.payRateMax;
  }
  public set payRateMax(value: number | null) {
    this.copilotActivationStore.localJobData.payRateMax = value;
  }
  public get payRateSalaryMin(): number | null {
    return this.copilotActivationStore.localJobData.payRateSalaryMin;
  }
  public set payRateSalaryMin(value: number | null) {
    this.copilotActivationStore.localJobData.payRateSalaryMin = value;
  }
  public get payRateSalaryMax(): number | null {
    return this.copilotActivationStore.localJobData.payRateSalaryMax;
  }
  public set payRateSalaryMax(value: number | null) {
    this.copilotActivationStore.localJobData.payRateSalaryMax = value;
  }
  public get payRatePeriod(): PayRatePeriod {
    return this.copilotActivationStore.localJobData.payRatePeriod;
  }
  public set payRatePeriod(value: PayRatePeriod) {
    this.copilotActivationStore.localJobData.payRatePeriod = value;
  }

  // Location
  public get street1(): string | null {
    return this.copilotActivationStore.localJobData.street1;
  }

  public set street1(value: string) {
    this.copilotActivationStore.localJobData.street1 = value;
  }

  public get city(): string | null {
    return this.copilotActivationStore.localJobData.city;
  }

  public set city(value: string) {
    this.copilotActivationStore.localJobData.city = value;
  }

  public get state(): string | null {
    return this.copilotActivationStore.localJobData.state;
  }

  public set state(value: string) {
    this.copilotActivationStore.localJobData.state = value;
  }

  public get postalCode(): string | null {
    return this.copilotActivationStore.localJobData.postalCode;
  }

  public set postalCode(value: string) {
    this.copilotActivationStore.localJobData.postalCode = value;
  }

  public get locationContext(): JobLocationContext | null {
    return this.copilotActivationStore.localJobData.locationContext;
  }

  // The typed address is kept across branch switches; the persistence layer nulls
  // whatever the chosen context forbids at submit time.
  public set locationContext(value: JobLocationContext | null) {
    this.copilotActivationStore.localJobData.locationContext = value;
    if (!isFieldContext(value)) {
      this.copilotActivationStore.localJobData.travelExpectations = null;
    }
    // Nationwide has no sourcing area, so a radius picked under lives_near must not
    // ride along in the snapshot (the default is stored as "absent").
    if (value === JobLocationContext.NATIONWIDE) {
      this.projectCopilotConfigurationV1OutreachRadiusMiles = DEFAULT_OUTREACH_RADIUS_MILES;
    }
    this.syncTravelDisclosure();
  }

  // "Where is the work performed?" A resolved context implies it; only an unresolved (null)
  // context needs the recorded answer, so "In the field" with no second answer yet is not
  // mistaken for the legacy worksite that null otherwise reads as.
  public get workPerformed(): WorkPerformed {
    const context = this.copilotActivationStore.localJobData.locationContext;
    return context === null
      ? this.copilotActivationStore.workPerformedAnswer
      : workPerformedFor(context);
  }

  public set workPerformed(value: WorkPerformed) {
    this.copilotActivationStore.workPerformedAnswer = value;
  }

  // What is sent for the job. Every job starts as a worksite job: a job that never got a context
  // (created from the dialog, quick-create or before contexts existed) is saved as fixed_location
  // as long as the recruiter left "At one worksite" selected; "In the field" with no second
  // answer stays unresolved (null).
  public get locationContextToSave(): JobLocationContext | null {
    const context = this.copilotActivationStore.localJobData.locationContext;
    if (context) return context;
    const derived = locationContextFor(this.workPerformed, workerLivesFor(context));
    // A worksite job is pinned to its ZIP and ff-rest rejects fixed_location without one. Save &
    // exit on a draft skips the form validation, so a draft saved before the ZIP is typed keeps
    // its null (legacy) context and becomes fixed_location on the first save that carries a ZIP.
    if (derived === JobLocationContext.FIXED_LOCATION && !this.postalCode?.trim()) return null;
    return derived;
  }

  public get travelExpectations(): JobTravelExpectations | null {
    return this.copilotActivationStore.localJobData.travelExpectations;
  }

  public set travelExpectations(value: JobTravelExpectations | null) {
    this.copilotActivationStore.localJobData.travelExpectations = value;
    this.syncTravelDisclosure();
  }

  // A field job gets one editable paragraph about the work arrangement in its description, so
  // boards that read it from the text (Indeed) get it by default. Only our own unedited
  // paragraph is ever replaced or removed; the customer's words stay theirs.
  private syncTravelDisclosure(): void {
    const store = this.copilotActivationStore;
    const next = travelDisclosureText(
      store.localJobData.locationContext,
      store.localJobData.travelExpectations,
    );
    const current = {
      description: store.localJobData.description ?? '',
      inserted: store.insertedTravelDisclosure,
    };
    const result = syncTravelDisclosure(current, next);
    store.insertedTravelDisclosure = result.inserted;
    if (result.description !== current.description) {
      this.replaceDescription(result.description);
    }
  }

  // A generated description replaces whatever was in the editor, our paragraph included, so it
  // is applied here and the disclosure re-synced on top instead of the editor swapping the text
  // behind the store's back.
  public applyGeneratedJobDescription(generated: string): void {
    this.copilotActivationStore.descriptionBeforeGeneration = null;
    this.replaceDescription(cleanHTMLJobDescription(generated));
    this.copilotActivationStore.insertedTravelDisclosure = null;
    this.syncTravelDisclosure();
  }

  // While generating, the editor sits under a "Let the AI magic begin" overlay; an empty editor
  // keeps the previous text (travel paragraph included) from showing around it. The text comes
  // back untouched if the generation is stopped or never delivers.
  private clearDescriptionForGeneration(): void {
    const store = this.copilotActivationStore;
    store.descriptionBeforeGeneration = {
      description: store.localJobData.description ?? '',
      insertedTravelDisclosure: store.insertedTravelDisclosure,
    };
    store.insertedTravelDisclosure = null;
    this.replaceDescription('');
  }

  private restoreDescriptionAfterGeneration(): void {
    const store = this.copilotActivationStore;
    const previous = store.descriptionBeforeGeneration;
    store.descriptionBeforeGeneration = null;
    if (!previous) return;
    this.replaceDescription(previous.description);
    store.insertedTravelDisclosure = previous.insertedTravelDisclosure;
  }

  private replaceDescription(description: string): void {
    const store = this.copilotActivationStore;
    const cleaned = cleanHTMLJobDescription(description);
    store.localJobData.description = cleaned;
    store.localJobData.rawDescription = makeMarkdownVersionOfJobDescription(cleaned);
    store.localJobDescription = cleaned;
  }

  public get anonymous(): boolean {
    return this.copilotActivationStore.localJobData.anonymous;
  }

  public set anonymous(value: boolean) {
    this.copilotActivationStore.localJobData.anonymous = value;
  }

  // Knowledge disciplines
  public get knowledgeDisciplines(): number[] {
    return this.copilotActivationStore.localKnowledgeDisciplines.sort();
  }

  public set knowledgeDisciplines(value: number[]) {
    this.copilotActivationStore.localKnowledgeDisciplines = value;
  }

  public get customKnowledgeDisciplines(): string[] {
    return [...this.copilotActivationStore.localCustomKnowledgeDisciplines].sort((a, b) =>
      a.localeCompare(b),
    );
  }

  public set customKnowledgeDisciplines(value: string[]) {
    this.copilotActivationStore.localCustomKnowledgeDisciplines = value;
  }

  // Machines & Brands
  public get machines(): number[] {
    return this.copilotActivationStore.localMachines.sort();
  }

  public set machines(value: number[]) {
    this.copilotActivationStore.localMachines = value;
  }

  public get customMachines(): string[] {
    return [...this.copilotActivationStore.localCustomMachines].sort((a, b) => a.localeCompare(b));
  }

  public set customMachines(value: string[]) {
    this.copilotActivationStore.localCustomMachines = value;
  }

  public get brandsPerMachine(): { [key: number]: number[] } {
    return this.copilotActivationStore.localBrandsPerMachine;
  }
  // This sort will help us know if there are changes in the brands
  // to do a Form isDirty check
  public get orderedBrandsPerMachine(): { [key: number]: number[] } {
    const result: { [key: number]: number[] } = {};
    for (const machineId in this.copilotActivationStore.localBrandsPerMachine) {
      if (
        Object.prototype.hasOwnProperty.call(
          this.copilotActivationStore.localBrandsPerMachine,
          machineId,
        )
      ) {
        const brands = this.copilotActivationStore.localBrandsPerMachine[machineId];
        result[machineId] = brands.slice().sort((a, b) => a - b); // Create a copy and sort
      }
    }

    return result;
  }

  public set brandsPerMachine(value: { [key: number]: number[] }) {
    this.copilotActivationStore.localBrandsPerMachine = value;
  }

  // Candidate Screening
  public get screeningAction(): ScreeningAction | null {
    return this.copilotActivationStore.localScreeningAction;
  }

  public set screeningAction(value: ScreeningAction | null) {
    this.copilotActivationStore.localScreeningAction = value;
  }

  // Screening type
  public get screeningType(): ScreeningTypeWithSkip | null {
    return this.copilotActivationStore.localScreeningType;
  }

  public set screeningType(value: ScreeningTypeWithSkip | null) {
    this.copilotActivationStore.localScreeningType = value;
  }

  public get employerCareerSiteUrl(): string | null {
    return this.copilotActivationStore.localEmployerCareerSiteUrl;
  }

  public set employerCareerSiteUrl(value: string | null) {
    this.copilotActivationStore.localEmployerCareerSiteUrl = value;
  }

  public get requireCopilotActivationChangeConfirmation(): boolean {
    const screeningAction = this.screeningAction ?? ScreeningAction.SCREEN;

    if (screeningAction !== ScreeningAction.SCREEN) {
      return false;
    }
    const projectHasCopilot = this.projectService.currentProject?.copilot ?? false;

    // change this to always fire if screening type changed
    return this.originalScreeningType === ScreeningType.DYNAMIC && projectHasCopilot;
  }

  public get jobUsesScreening(): boolean {
    const screeningAction = this.screeningAction ?? ScreeningAction.SCREEN;
    if (screeningAction !== ScreeningAction.SCREEN) {
      return false;
    }
    // consider screeningType = SKIP as not doing screening
    return (
      this.screeningType === ScreeningType.DYNAMIC || this.screeningType === ScreeningType.STATIC
    );
  }

  // Screener Questions
  public get screenerQuestions(): LocalScreenerQuestion[] {
    return this.copilotActivationStore.localScreenerQuestions;
  }

  public set screenerQuestions(value: LocalScreenerQuestion[]) {
    this.copilotActivationStore.localScreenerQuestions = value;
  }

  // Job Description
  public get description(): string | null {
    return this.copilotActivationStore.localJobData.description;
  }

  public set description(value: string | null) {
    this.copilotActivationStore.localJobData.description = value;
  }

  public get rawDescription(): string | null {
    return this.copilotActivationStore.localJobData.rawDescription;
  }

  public set rawDescription(value: string | null) {
    this.copilotActivationStore.localJobData.rawDescription = value;
  }

  public get localJobDescription(): string {
    return this.copilotActivationStore.localJobDescription ?? '';
  }

  public set localJobDescription(value: string) {
    this.copilotActivationStore.localJobDescription = value;
  }

  // Job Description Generation
  public get isGeneratingJobDescription(): boolean {
    return this.copilotActivationStore.isGeneratingJobDescription;
  }

  public get generatedJobDescriptionDocument(): GeneratedJobDescriptionDocument | null {
    return this.copilotActivationStore.generatedJobDescriptionDocument;
  }

  public set stopGeneratingJobDescriptionFlag(value: boolean) {
    this.copilotActivationStore.stopGeneratingJobDescriptionFlag = value;
  }

  // Screener Questions Generation
  public get isGeneratingScreenerQuestions(): boolean {
    return this.copilotActivationStore.isGeneratingScreenerQuestions;
  }

  public get generatedScreenerQuestionsDocument(): GeneratedScreenerQuestionsDocument | null {
    return this.copilotActivationStore.generatedScreenerQuestionsDocument;
  }

  public set stopGeneratingScreenerQuestionsFlag(value: boolean) {
    this.copilotActivationStore.stopGeneratingScreenerQuestionsFlag = value;
  }

  public get scheduledUpdates(): boolean {
    return this.copilotActivationStore.scheduledUpdates;
  }

  public set scheduledUpdates(value: boolean) {
    this.copilotActivationStore.scheduledUpdates = value;
  }

  // Active Outreach Enabled
  public get originalActiveOutreachEnabled(): boolean {
    return this.copilotActivationStore.selectedJob?.activeOutreachEnabled ?? false;
  }

  public get activeOutreachEnabled(): boolean {
    return this.copilotActivationStore.activeOutreachEnabled;
  }

  public set activeOutreachEnabled(value: boolean) {
    this.copilotActivationStore.activeOutreachEnabled = value;
  }

  // Project Copilot Configuration
  public get copilotConfigurationTemplateId(): number | undefined {
    return this.copilotActivationStore.copilotConfigurationTemplateId;
  }

  public set copilotConfigurationTemplateId(value: number | undefined) {
    this.copilotActivationStore.copilotConfigurationTemplateId = value;
  }

  public get projectCopilotConfiguration(): ProjectCopilotConfigurationV1Snapshot {
    return this.copilotActivationStore.projectCopilotConfiguration;
  }

  public set projectCopilotConfiguration(value: ProjectCopilotConfigurationV1Snapshot) {
    this.copilotActivationStore.projectCopilotConfiguration = value;
  }

  // Outreach radius (sourcing radius) — see ONE-6000.
  // Reads return the explicit value or the default (30) so the UI always has a
  // pill to highlight. Writes treat the default as no-op (cleared) so the
  // persisted snapshot stays absent for the default.
  public get projectCopilotConfigurationV1OutreachRadiusMiles(): V1SnapshotOutreachRadius {
    return this.projectCopilotConfiguration?.outreachRadiusMiles ?? DEFAULT_OUTREACH_RADIUS_MILES;
  }

  public set projectCopilotConfigurationV1OutreachRadiusMiles(value: unknown) {
    if (!isOutreachRadiusOption(value)) {
      return;
    }
    if (value === DEFAULT_OUTREACH_RADIUS_MILES) {
      delete this.projectCopilotConfiguration.outreachRadiusMiles;
    } else {
      this.projectCopilotConfiguration.outreachRadiusMiles = value;
    }
  }

  public get projectCopilotConfigurationName(): string | undefined {
    return this.projectCopilotConfiguration?.copilotName ?? 'Copilot';
  }

  public set projectCopilotConfigurationName(value: string) {
    this.projectCopilotConfiguration.copilotName = value;
  }

  public get projectCopilotConfigurationV1Strictness():
    | ProjectCopilotConfigurationV1Snapshot['strictness']
    | null {
    return this.projectCopilotConfiguration?.strictness ?? null;
  }

  public set projectCopilotConfigurationV1Strictness(value: unknown) {
    if (isEnumMember(V1SnapshotStrictness, value)) {
      this.projectCopilotConfiguration.strictness = value;
    }
  }

  public get projectCopilotConfigurationV1RelevantSkills(): string | null {
    return this.projectCopilotConfiguration?.relevantSkills ?? null;
  }

  public set projectCopilotConfigurationV1RelevantSkills(value: string) {
    this.projectCopilotConfiguration.relevantSkills = value;
  }

  public get projectCopilotConfigurationV1ImmediateRejectionReason(): string | null {
    return this.projectCopilotConfiguration?.immediateRejectionReason ?? null;
  }

  public set projectCopilotConfigurationV1ImmediateRejectionReason(value: string) {
    this.projectCopilotConfiguration.immediateRejectionReason = value;
  }

  public get projectCopilotConfigurationV1ScoreThreshold():
    | ProjectCopilotConfigurationV1Snapshot['scoreThreshold']
    | null {
    return this.projectCopilotConfiguration?.scoreThreshold ?? null;
  }

  public set projectCopilotConfigurationV1ScoreThreshold(value: unknown) {
    if (isEnumMember(V1SnapshotScoreThreshold, value)) {
      this.projectCopilotConfiguration.scoreThreshold = value;
    }
  }

  public get projectCopilotConfigurationV1TargetCompanies(): string | null {
    return this.projectCopilotConfiguration?.targetCompanies ?? null;
  }

  public set projectCopilotConfigurationV1TargetCompanies(value: string) {
    this.projectCopilotConfiguration.targetCompanies = value;
  }

  public get projectCopilotConfigurationV1AvoidCompanies(): string | null {
    return this.projectCopilotConfiguration?.avoidCompanies ?? null;
  }

  public set projectCopilotConfigurationV1AvoidCompanies(value: string) {
    this.projectCopilotConfiguration.avoidCompanies = value;
  }

  public get projectCopilotConfigurationV1CopilotName(): string | null {
    return this.projectCopilotConfiguration?.copilotName ?? null;
  }

  public set projectCopilotConfigurationV1CopilotName(value: string) {
    this.projectCopilotConfiguration.copilotName = value;
  }

  public get projectCopilotConfigurationV1CommunicationTone():
    | ProjectCopilotConfigurationV1Snapshot['communicationTone']
    | null {
    return this.projectCopilotConfiguration?.communicationTone ?? null;
  }

  public set projectCopilotConfigurationV1CommunicationTone(value: unknown) {
    if (isEnumMember(V1SnapshotCommunicationTone, value)) {
      this.projectCopilotConfiguration.communicationTone = value;
    }
  }

  public get projectCopilotConfigurationV1Pushiness():
    | ProjectCopilotConfigurationV1Snapshot['pushiness']
    | null {
    return this.projectCopilotConfiguration?.pushiness ?? null;
  }

  public set projectCopilotConfigurationV1Pushiness(value: unknown) {
    if (isEnumMember(V1SnapshotPushiness, value)) {
      this.projectCopilotConfiguration.pushiness = value;
    }
  }

  public get projectCopilotConfigurationV1Thoroughness():
    | ProjectCopilotConfigurationV1Snapshot['thoroughness']
    | null {
    return this.projectCopilotConfiguration?.thoroughness ?? null;
  }

  public set projectCopilotConfigurationV1Thoroughness(value: unknown) {
    if (isEnumMember(V1SnapshotThoroughness, value)) {
      this.projectCopilotConfiguration.thoroughness = value;
    }
  }

  public get projectCopilotConfigurationV1MessagingWindow():
    | ProjectCopilotConfigurationV1Snapshot['messagingWindow']
    | null {
    return this.projectCopilotConfiguration?.messagingWindow ?? null;
  }

  public set projectCopilotConfigurationV1MessagingWindow(value: unknown) {
    if (isEnumMember(V1SnapshotMessagingWindow, value)) {
      this.projectCopilotConfiguration.messagingWindow = value;
    }
  }

  public get projectCopilotConfigurationV1SellingPoints(): string | null {
    return this.projectCopilotConfiguration?.sellingPoints ?? null;
  }

  public set projectCopilotConfigurationV1SellingPoints(value: string) {
    this.projectCopilotConfiguration.sellingPoints = value;
  }

  public get projectCopilotConfigurationV1PerksAndPrograms(): string | null {
    return this.projectCopilotConfiguration?.perksAndPrograms ?? null;
  }

  public set projectCopilotConfigurationV1PerksAndPrograms(value: string) {
    this.projectCopilotConfiguration.perksAndPrograms = value;
  }

  public get projectCopilotConfigurationV1IncludeCandidateAiDisclosure(): boolean {
    return this.projectCopilotConfiguration.includeAiDisclosure === true;
  }

  public set projectCopilotConfigurationV1IncludeCandidateAiDisclosure(value: boolean) {
    this.projectCopilotConfiguration.includeAiDisclosure = value;
  }

  public get projectCopilotConfigurationV1DynamicScreeningQuestions(): DynamicScreeningQuestion[] {
    return this.projectCopilotConfiguration.dynamicScreeningQuestions ?? [];
  }

  public set projectCopilotConfigurationV1DynamicScreeningQuestions(
    questions: DynamicScreeningQuestion[],
  ) {
    this.projectCopilotConfiguration.dynamicScreeningQuestions =
      this.normalizeDynamicScreeningQuestions(questions);
  }

  public get projectCopilotConfigurationV1RelevantBackground(): string | null {
    return this.projectCopilotConfiguration?.relevantBackground ?? null;
  }

  public set projectCopilotConfigurationV1RelevantBackground(value: string) {
    this.projectCopilotConfiguration.relevantBackground = value;
  }

  // AI learnings getter/setter pairs for editable display in job form
  // These access the aiRecruiterLearnings object from projectCopilotConfiguration.aiRecruiterLearnings
  // Users can view and edit these AI-generated insights
  private getDefaultAiRecruiterLearnings(): AiRecruiterLearnings {
    return {
      relevantSkills: '',
      relevantBackground: '',
      immediateRejectionReason: '',
      targetCompanies: '',
      avoidCompanies: '',
    };
  }
  private getCurrentAiRecruiterLearnings(): AiRecruiterLearnings {
    if (!this.projectCopilotConfiguration.aiRecruiterLearnings) {
      this.projectCopilotConfiguration.aiRecruiterLearnings = this.getDefaultAiRecruiterLearnings();
    }
    return this.projectCopilotConfiguration.aiRecruiterLearnings;
  }

  public get aiLearningsRelevantSkills(): string | null {
    return this.getCurrentAiRecruiterLearnings().relevantSkills;
  }

  public set aiLearningsRelevantSkills(value: string) {
    this.getCurrentAiRecruiterLearnings().relevantSkills = value;
  }

  public get aiLearningsRelevantBackground(): string | null {
    return this.getCurrentAiRecruiterLearnings().relevantBackground;
  }

  public set aiLearningsRelevantBackground(value: string) {
    this.getCurrentAiRecruiterLearnings().relevantBackground = value;
  }

  public get aiLearningsImmediateRejectionReason(): string | null {
    return this.getCurrentAiRecruiterLearnings().immediateRejectionReason;
  }

  public set aiLearningsImmediateRejectionReason(value: string) {
    this.getCurrentAiRecruiterLearnings().immediateRejectionReason = value;
  }

  public get aiLearningsTargetCompanies(): string | null {
    return this.getCurrentAiRecruiterLearnings().targetCompanies;
  }

  public set aiLearningsTargetCompanies(value: string) {
    this.getCurrentAiRecruiterLearnings().targetCompanies = value;
  }

  public get aiLearningsAvoidCompanies(): string | null {
    return this.getCurrentAiRecruiterLearnings().avoidCompanies;
  }

  public set aiLearningsAvoidCompanies(value: string) {
    this.getCurrentAiRecruiterLearnings().avoidCompanies = value;
  }

  // Interview Automation Settings
  public get interviewSettings(): ProjectInterviewSettings | undefined {
    return this.copilotActivationStore.localInterviewSettings;
  }

  public set interviewSettings(settings: ProjectInterviewSettings | null) {
    this.copilotActivationStore.localInterviewSettings = settings ?? undefined;
  }

  public get interviewSettingsEnabled(): boolean {
    return this.copilotActivationStore.localInterviewSettings?.enabled ?? false;
  }

  public set interviewSettingsEnabled(value: boolean) {
    if (!this.copilotActivationStore.localInterviewSettings) {
      this.copilotActivationStore.localInterviewSettings = this.getDefaultInterviewSettings();
    }
    this.copilotActivationStore.localInterviewSettings.enabled = value;
  }

  public get interviewSettingsScoreThreshold(): number {
    const defaultSettings = this.getDefaultInterviewSettings();
    return (
      this.copilotActivationStore.localInterviewSettings?.scoreThreshold ??
      defaultSettings.scoreThreshold
    );
  }

  public set interviewSettingsScoreThreshold(value: number) {
    if (!this.copilotActivationStore.localInterviewSettings) {
      this.copilotActivationStore.localInterviewSettings = this.getDefaultInterviewSettings();
    }
    this.copilotActivationStore.localInterviewSettings.scoreThreshold = value;
  }

  public getDefaultInterviewSettings(): ProjectInterviewSettings {
    const currentUser = this.meService.userProfile;
    return {
      enabled: false,
      scoreThreshold: 4,
      contacts: [
        {
          userProfileId: currentUser?.id ?? null,
          name: 'Your Calendar',
          email: currentUser?.email ?? '',
        },
      ],
      duration: null,
      type: null,
      venueDetails: '',
      additionalDetails: '',
      schedulingPreferences: '',
      requireFinalConfirmation: true,
      recruiterEmail: this.meService.userProfile?.email ?? '',
      recruiterName: getUserName(this.meService.userProfile) ?? '',
      recruiterUserProfileId: this.meService.userProfile?.id ?? null,
    };
  }

  public resetInterviewSettings(): void {
    this.copilotActivationStore.localInterviewSettings = undefined;
  }
  // ====== END: DATA MAPPING ======

  async setupStoreByProject(
    project: Project,
    useJobVersion = false,
  ): Promise<JobWithRelations<'jobStatusHistories'> | undefined> {
    const projectId = project.id;
    const jobId = project.jobId;
    let job;
    let customSkills: JobCustomSkill[] = [];
    const isJobVersion = useJobVersion && !!this.jobVersionsService.jobVersionWithRelations;

    if (isJobVersion) {
      job = this.jobVersionsService.jobVersionWithRelations;
      customSkills = this.jobVersionsService.jobVersionWithRelations?.jobCustomSkills ?? [];
      this.tempCustomMachines = (
        this.jobVersionsService.jobVersionWithRelations?.jobCustomMachines ?? []
      ).map((m) => m.title);

      const projectCopilotConfiguration =
        this.jobVersionsService.jobVersionWithRelations?.projectCopilotConfiguration;

      const projectCopilotConfigurationSnapshot =
        ProjectCopilotConfigurationUtils.parseProjectCopilotConfiguration(
          projectCopilotConfiguration,
        );

      this.projectCopilotConfiguration = projectCopilotConfigurationSnapshot;
      this.copilotActivationStore.copilotConfigurationTemplateId =
        projectCopilotConfiguration?.copilotConfigurationTemplateId;
    } else {
      this.loadingProjectId = projectId;
      let customMachines: JobCustomMachine[];
      [job, customSkills, customMachines] = await Promise.all([
        this.jobPersistence.getByIdWithRelations(jobId, [
          'jobTitle',
          'screenerQuestions',
          'jobQualifications',
          'jobResponsibilities',
          'jobTaxonomyMachineMaps',
          'jobTaxonomyKnowledgeDisciplineMaps',
          'jobStatusHistories',
        ]),
        this.customSkillPersistence.getProjectCustomSkills(project.employerId, projectId),
        this.customMachinePersistence.getProjectCustomMachines(project.employerId, projectId),
      ]);
      this.tempCustomMachines = customMachines.map((m) => m.title);

      if (this.loadingProjectId !== projectId) {
        return;
      }

      const projectCopilotConfiguration =
        this.projectService.currentProject?.projectCopilotConfiguration;

      const projectCopilotConfigurationSnapshot =
        ProjectCopilotConfigurationUtils.parseProjectCopilotConfiguration(
          projectCopilotConfiguration,
        );

      this.projectCopilotConfiguration = projectCopilotConfigurationSnapshot;
      this.copilotActivationStore.copilotConfigurationTemplateId =
        projectCopilotConfiguration?.copilotConfigurationTemplateId;
    }

    if (!job) {
      this.copilotActivationStore.selectedProjectId = null;
      this.copilotActivationStore.selectedJobId = null;
      this.copilotActivationStore.selectedJob = null;
      this.resetStoreData();
    } else {
      this.resetStoreData(isJobVersion);

      // Fetch needed data for the job
      this.copilotActivationStore.jobTitles = await this.jobTitleService.fetchJobTitles();

      this.copilotActivationStore.selectedProjectId = projectId;
      this.copilotActivationStore.selectedJobId = jobId;
      this.copilotActivationStore.selectedJob = job;

      this.mapJobDetailsData(job);

      if (this.copilotActivationStore.localJobData.displayTitle) {
        const jobTitle = await this.fetchRelatedJobTitleForDisplayTitle(
          this.copilotActivationStore.localJobData.displayTitle,
        );
        this.copilotActivationStore.localJobData.jobTitleId = jobTitle?.id ?? null;
        this.copilotActivationStore.selectedJobTitle = jobTitle;
      }

      this.mapSkillkeywordsData(job, customSkills);

      // Should show candidate screening or screener questions
      const isDoingCandidateScreening = this.employerFeatureService.hasUrlScreeningEnabled;
      this.mapCandidateScreeningData(job, isDoingCandidateScreening);

      this.mapScreeningQuestionsData(job);

      this.mapJobDescriptionData(job);
    }
    this.copilotActivationStore.scheduledUpdates = project.scheduledUpdates;

    await this.employerService.fetchEmployerPolicies();
    this.enforceEmployerPolicy();

    return job;
  }

  private normalizeDynamicScreeningQuestions(
    questions?: DynamicScreeningQuestion[],
  ): DynamicScreeningQuestion[] {
    if (!questions) {
      return [];
    }

    return questions.map((question, index) => ({
      ...question,
      order: index + 1,
    }));
  }

  private resetStoreData(isJobVersion = false): void {
    if (!isJobVersion) {
      this.copilotActivationStore.hasTriedToUpdateJob = false;
      this.copilotActivationStore.selectedProjectId = null;
      this.copilotActivationStore.selectedJobId = null;
      this.copilotActivationStore.selectedJob = null;
      this.copilotActivationStore.selectedJobTitle = null;
      this.copilotActivationStore.jobDescriptionDocumentId = '';
      this.copilotActivationStore.generatedJobDescriptionDocument = null;
      this.copilotActivationStore.jobDescriptionPollingIntervalId = null;
      this.copilotActivationStore.isGeneratingJobDescription = false;
      this.copilotActivationStore.stopGeneratingJobDescriptionFlag = false;
      this.copilotActivationStore.generatedScreenerQuestionsDocumentId = '';
      this.copilotActivationStore.generatedScreenerQuestionsDocument = null;
      this.copilotActivationStore.screenerQuestionsPollingIntervalId = null;
      this.copilotActivationStore.isGeneratingScreenerQuestions = false;
      this.copilotActivationStore.stopGeneratingScreenerQuestionsFlag = false;
      this.copilotActivationStore.jobStatus = null;
      this.copilotActivationStore.activeOutreachEnabled = false;
      this.copilotActivationStore.workPerformedAnswer = 'worksite';
      this.copilotActivationStore.insertedTravelDisclosure = null;

      this.copilotActivationStore.localJobData = {
        employerName: '',
        jobTitleId: null,
        displayTitle: null,
        employerAssigneeId: null,
        hireType: null,
        shift: null,
        startTime: null,
        endTime: null,
        description: '',
        rawDescription: '',
        payRateMin: null,
        payRateMax: null,
        payRateSalaryMin: null,
        payRateSalaryMax: null,
        payRatePeriod: PayRatePeriod.HOURLY,
        minimumApplicantsPerWeek: 0,
        hiresPerPosition: 0,
        street1: '',
        city: '',
        state: '',
        postalCode: '',
        locationContext: null,
        travelExpectations: null,
        anonymous: false,
      };
    }
    this.copilotActivationStore.localJobDescription = '';
    this.copilotActivationStore.localKnowledgeDisciplines = [];
    this.copilotActivationStore.localMachines = [];
    this.copilotActivationStore.localCustomKnowledgeDisciplines = [];
    this.copilotActivationStore.localCustomMachines = [];
    this.copilotActivationStore.localBrandsPerMachine = {};
    this.copilotActivationStore.localScreeningAction = null;
    this.copilotActivationStore.localEmployerCareerSiteUrl = null;
    this.copilotActivationStore.localScreenerQuestions = [];
    this.copilotActivationStore.jobTitles = [];
    this.resetInterviewSettings();
  }

  private mapJobDetailsData(job: Job): void {
    this.copilotActivationStore.jobStatus = job.status ?? null;
    this.copilotActivationStore.localJobData.displayTitle = job.displayTitle ?? '';
    this.copilotActivationStore.localJobData.hireType = job.hireType ?? null;
    this.copilotActivationStore.localJobData.shift = job.shift ?? null;
    this.copilotActivationStore.localJobData.anonymous = job.anonymous ?? false;

    if (job.hireType === HireType.PROJECT) {
      this.copilotActivationStore.localJobData.shift = 4;
    }

    // Shift option "Multiple shifts" is stored as null in the db but set a value of -1 in the UI
    if (
      job.hireType &&
      [HireType.FULL_TIME, HireType.PART_TIME].includes(job.hireType) &&
      job.shift === null
    ) {
      this.copilotActivationStore.localJobData.shift = -1;
    }

    if (job.shift === 0) {
      this.copilotActivationStore.localJobData.startTime = job.startTime ?? null;
      this.copilotActivationStore.localJobData.endTime = job.endTime ?? null;
    } else {
      this.copilotActivationStore.localJobData.startTime = null;
      this.copilotActivationStore.localJobData.endTime = null;
    }

    this.copilotActivationStore.localJobData.payRateMin = job.payRateMin ?? null;
    this.copilotActivationStore.localJobData.payRateMax = job.payRateMax ?? null;
    this.copilotActivationStore.localJobData.payRateSalaryMin = job.payRateSalaryMin ?? null;
    this.copilotActivationStore.localJobData.payRateSalaryMax = job.payRateSalaryMax ?? null;
    this.copilotActivationStore.localJobData.payRatePeriod =
      job.payRatePeriod ?? PayRatePeriod.HOURLY;

    this.copilotActivationStore.localJobData.street1 = job.street1 ?? '';
    this.copilotActivationStore.localJobData.city = job.city ?? '';
    this.copilotActivationStore.localJobData.state = job.state ?? '';
    this.copilotActivationStore.localJobData.postalCode = job.postalCode ?? '';
    this.copilotActivationStore.localJobData.locationContext = job.locationContext ?? null;
    this.copilotActivationStore.localJobData.travelExpectations = job.travelExpectations ?? null;
    // A hydrated null context is a legacy worksite job, never a half-answered field one.
    this.copilotActivationStore.workPerformedAnswer = 'worksite';

    this.copilotActivationStore.activeOutreachEnabled = job.activeOutreachEnabled ?? false;
  }

  private mapSkillkeywordsData(
    job: JobWithRelations<
      | 'jobTitle'
      | 'screenerQuestions'
      | 'jobQualifications'
      | 'jobResponsibilities'
      | 'jobTaxonomyKnowledgeDisciplineMaps'
      | 'jobTaxonomyMachineMaps'
    >,
    customSkills: JobCustomSkill[] = [],
  ): void {
    if (job.jobTaxonomyKnowledgeDisciplineMaps) {
      this.copilotActivationStore.localKnowledgeDisciplines =
        job.jobTaxonomyKnowledgeDisciplineMaps.map((knowledgeDiscipline) => {
          return knowledgeDiscipline.taxonomyKnowledgeDisciplineId;
        });
    }

    if (job.jobTaxonomyMachineMaps) {
      for (const machine of job.jobTaxonomyMachineMaps) {
        this.copilotActivationStore.localMachines.push(machine.taxonomyMachineId);
        this.copilotActivationStore.localBrandsPerMachine[machine.taxonomyMachineId] = [];

        if (
          Object.prototype.hasOwnProperty.call(machine, 'jobTaxonomyMachineMapTaxonomyBrandMaps')
        ) {
          for (const brand of (
            machine as JobTaxonomyMachineMapWithRelations<'jobTaxonomyMachineMapTaxonomyBrandMaps'>
          ).jobTaxonomyMachineMapTaxonomyBrandMaps) {
            this.copilotActivationStore.localBrandsPerMachine[machine.taxonomyMachineId].push(
              brand.taxonomyBrandId,
            );
          }
        }
      }
    }

    this.mapCustomSkillData(customSkills);
  }

  private mapCustomSkillData(customSkills: JobCustomSkill[]): void {
    this.copilotActivationStore.localCustomKnowledgeDisciplines = customSkills
      .filter((customSkill) => customSkill.field === JobCustomSkillField.KNOWLEDGE_DISCIPLINE)
      .map((customSkill) => customSkill.title);
    this.copilotActivationStore.localCustomMachines = this.tempCustomMachines;
  }

  private mapCandidateScreeningData(
    job: JobWithRelations<'screenerQuestions'>,
    isDoingCandidateScreening: boolean,
  ): void {
    const noQuestionAdded =
      (job.screenerQuestions || []).filter((q) => Boolean(q.question)).length === 0;
    const skipScreening = job.screeningType === ScreeningType.STATIC && noQuestionAdded;

    if (skipScreening) {
      this.copilotActivationStore.localScreeningType = 'SKIP';
    } else {
      this.copilotActivationStore.localScreeningType = job.screeningType ?? ScreeningType.STATIC;
    }

    if (!isDoingCandidateScreening) {
      this.copilotActivationStore.localScreeningAction = null;
      this.copilotActivationStore.localEmployerCareerSiteUrl = null;
    } else {
      this.copilotActivationStore.localScreeningAction = job.screeningAction ?? null;
      if (job.screeningAction === ScreeningAction.URL) {
        this.copilotActivationStore.localEmployerCareerSiteUrl = job.employerCareerSiteUrl ?? '';
      } else if (job.screeningAction === ScreeningAction.SCREEN) {
        this.copilotActivationStore.localEmployerCareerSiteUrl = null;
      }
    }
  }

  private mapScreeningQuestionsData(job: JobWithRelations<'screenerQuestions'>): void {
    let questionId = 1;
    if (job.screenerQuestions) {
      for (const question of job.screenerQuestions) {
        this.copilotActivationStore.localScreenerQuestions.push({
          text: question.question,
          id: questionId,
          editContent: question.question,
        });
        questionId += 1;
      }
    }

    // Have at least 3 questions in the UI
    if (
      this.copilotActivationStore.localScreenerQuestions.length <
      MINIMUM_REQUIRED_SCREENER_QUESTIONS
    ) {
      const difference =
        MINIMUM_REQUIRED_SCREENER_QUESTIONS -
        this.copilotActivationStore.localScreenerQuestions.length;

      for (let i = 0; i < difference; i++) {
        this.copilotActivationStore.localScreenerQuestions.push({
          text: '',
          id: questionId,
          editContent: '',
        });
        questionId += 1;
      }
    }
  }

  private mapJobDescriptionData(job: Job): void {
    /**
     * Parse locally the HTML description into markdown so we can
     * work with the raw md on the flow. Also handles jobs that do have
     * an HTML description, but no raw description stored on the db.
     */
    const cleanedDescription = cleanHTMLJobDescription(job.description ?? '');
    const rawDescription = makeMarkdownVersionOfJobDescription(cleanedDescription);

    this.copilotActivationStore.localJobData.description = cleanedDescription;
    this.copilotActivationStore.localJobData.rawDescription = rawDescription;

    this.copilotActivationStore.localJobDescription = cleanedDescription;

    try {
      const errors: string[] = [];

      if (JobDescriptionUtils.hasDescriptionBulletPointsInAnotherLine(cleanedDescription)) {
        errors.push(TrackingJobDescriptionParsingError.BULLET_POINT_IN_ANOTHER_LINE);
      }

      if (JobDescriptionUtils.hasBoldAsterisksNotBeingFormatted(cleanedDescription)) {
        errors.push(TrackingJobDescriptionParsingError.BOLD_ASTERISKS_NOT_BEING_FORMATTED);
      }

      if (JobDescriptionUtils.hasBoldAsterisksInAnotherLine(cleanedDescription)) {
        errors.push(TrackingJobDescriptionParsingError.BOLD_ASTERISKS_IN_ANOTHER_LINE);
      }

      if (errors.length > 0) {
        TrackingService.trackAction(TrackingActionName.JOB_DESCRIPTION_PARSING_ERROR, {
          errors,
          originalDescription: job.description,
          errorDescription: cleanedDescription,
          source: TrackingJobDescriptionParsingErrorSource.EDITOR_INITIALIZATION_PARSING,
        });
      }
    } catch (error) {
      ErrorService.captureException(error);
    }
  }

  public async fetchRelatedJobTitleForDisplayTitle(displayTitle: string): Promise<JobTitle | null> {
    return this.jobTitleService.normalizeJobTitle(displayTitle);
  }

  private appendCustomSkillTitles(skills: string[]): void {
    for (const title of [
      ...this.copilotActivationStore.localCustomKnowledgeDisciplines,
      ...this.copilotActivationStore.localCustomMachines,
    ]) {
      this.pushUniqueSkillTitle(skills, title);
    }
  }

  private pushUniqueSkillTitle(skills: string[], title: string): void {
    const normalizedTitle = title.trim().toLocaleLowerCase();

    if (!normalizedTitle) {
      return;
    }

    const hasSkill = skills.some((skill) => skill.trim().toLocaleLowerCase() === normalizedTitle);
    if (!hasSkill) {
      skills.push(title);
    }
  }

  private buildCustomSkillPayload(): JobCustomSkill[] {
    return this.copilotActivationStore.localCustomKnowledgeDisciplines.map((title) => ({
      title,
      field: JobCustomSkillField.KNOWLEDGE_DISCIPLINE,
    }));
  }

  private buildCustomMachinePayload(): JobCustomMachine[] {
    return this.copilotActivationStore.localCustomMachines.map((title) => ({ title }));
  }

  // ====== START: JOB DESCRIPTION GENERATION ======
  public async generateJobDescription(): Promise<string | undefined> {
    // Reset polling
    this.clearJobDescriptionGenerationPolling();
    this.copilotActivationStore.generatedJobDescriptionDocument = null;
    this.copilotActivationStore.isGeneratingJobDescription = true;
    this.clearDescriptionForGeneration();

    const title = this.copilotActivationStore.localJobData.displayTitle ?? '';
    const city = this.copilotActivationStore.localJobData.city ?? '';
    const state = this.copilotActivationStore.localJobData.state ?? '';
    const postalCode = this.copilotActivationStore.localJobData.postalCode ?? '';
    const location = `${city}, ${state}, ${postalCode}`;
    const skills: string[] = [];
    const brands: string[] = [];

    for (const skillId of this.copilotActivationStore.localKnowledgeDisciplines) {
      const skill = this.taxonomyService.knowledgeDisciplines.find((knowledgeDiscipline) => {
        return knowledgeDiscipline.id === skillId;
      });
      if (skill) {
        skills.push(skill.title);
      }
    }

    for (const machineId of this.copilotActivationStore.localMachines) {
      const skill = this.taxonomyService.machines.find((machine) => {
        return machine.id === machineId;
      });
      if (skill) {
        skills.push(skill.title);

        const brandsForMachine = this.copilotActivationStore.localBrandsPerMachine[machineId];
        for (const brandId of brandsForMachine) {
          const brand = this.taxonomyService.brands.find((brand) => {
            return brand.id === brandId;
          });
          if (brand) {
            brands.push(brand.title);
          }
        }
      }
    }

    this.appendCustomSkillTitles(skills);

    const options: GenerateJobDescriptionOptions = {
      title: title,
      location: location,
      skills,
      brands,
    };

    const generatedDescriptionDocumentId =
      await this.jobPersistence.generateJobDescription(options);

    if (generatedDescriptionDocumentId) {
      // Start polling
      this.copilotActivationStore.jobDescriptionDocumentId = generatedDescriptionDocumentId;
      this.startPollingJobDescriptionGeneration(generatedDescriptionDocumentId);
    } else {
      this.copilotActivationStore.jobDescriptionDocumentId = '';
      this.copilotActivationStore.isGeneratingJobDescription = false;
      this.restoreDescriptionAfterGeneration();
    }

    return generatedDescriptionDocumentId;
  }

  public clearJobDescriptionGenerationPolling(): void {
    if (this.copilotActivationStore.jobDescriptionPollingIntervalId) {
      window?.clearInterval(this.copilotActivationStore.jobDescriptionPollingIntervalId);
    }

    this.copilotActivationStore.jobDescriptionPollingIntervalId = null;
    this.copilotActivationStore.jobDescriptionPollingTriedAttempts = 0;
    this.copilotActivationStore.isGeneratingJobDescription = false;
  }

  public async startPollingJobDescriptionGeneration(documentId: string): Promise<void> {
    this.copilotActivationStore.jobDescriptionPollingIntervalId = window?.setInterval(async () => {
      const generatedDescriptionDocument =
        this.copilotActivationStore.generatedJobDescriptionDocument;

      if (generatedDescriptionDocument?.jobDescription.fullfilled) {
        // Description already generated, apply it and stop polling
        this.applyGeneratedJobDescription(
          generatedDescriptionDocument.jobDescription.generatedJobDescription,
        );
        SnackbarService.success('Job description generated successfully');
        this.clearJobDescriptionGenerationPolling();
      } else {
        // Description not generated yet, keep polling
        const pollingAttempts = this.copilotActivationStore.jobDescriptionPollingTriedAttempts;
        const maxAttemps = this.copilotActivationStore.jobDescriptionPollingMaxAttempts;
        if (pollingAttempts > maxAttemps) {
          this.clearJobDescriptionGenerationPolling();
          this.copilotActivationStore.jobDescriptionDocumentId = '';
          this.restoreDescriptionAfterGeneration();
          SnackbarService.critical(
            'There was a problem generating the job description, try again later.',
          );
        } else if (this.copilotActivationStore.stopGeneratingJobDescriptionFlag) {
          this.clearJobDescriptionGenerationPolling();
          this.copilotActivationStore.jobDescriptionDocumentId = '';
          this.copilotActivationStore.stopGeneratingJobDescriptionFlag = false;
          this.restoreDescriptionAfterGeneration();
          SnackbarService.caution('Job description generation was stopped.');
        } else {
          this.copilotActivationStore.generatedJobDescriptionDocument =
            await this.jobPersistence.getGeneratedJobDescription(documentId);
          this.copilotActivationStore.jobDescriptionPollingTriedAttempts += 1;
        }
      }
    }, this.copilotActivationStore.jobDescriptionPollingIntervalTime);
  }

  public stopGeneratingJobDescription(): void {
    this.copilotActivationStore.stopGeneratingJobDescriptionFlag = true;
  }
  // ====== END: JOB DESCRIPTION GENERATION ======

  // ====== START: SCREENING QUESTIONS GENERATION ======
  public async generateScreenerQuestions(): Promise<string | undefined> {
    // Reset polling
    this.clearScreenerQuestionsGenerationPolling();
    this.copilotActivationStore.isGeneratingScreenerQuestions = true;
    this.copilotActivationStore.generatedScreenerQuestionsDocument = null;

    const title = this.copilotActivationStore.localJobData.displayTitle ?? '';
    const skills: string[] = [];
    const brands: string[] = [];

    for (const skillId of this.copilotActivationStore.localKnowledgeDisciplines) {
      const skill = this.taxonomyService.knowledgeDisciplines.find((knowledgeDiscipline) => {
        return knowledgeDiscipline.id === skillId;
      });
      if (skill) {
        skills.push(skill.title);
      }
    }

    for (const machineId of this.copilotActivationStore.localMachines) {
      const skill = this.taxonomyService.machines.find((machine) => {
        return machine.id === machineId;
      });
      if (skill) {
        skills.push(skill.title);

        const brandsForMachine = this.copilotActivationStore.localBrandsPerMachine[machineId];
        for (const brandId of brandsForMachine) {
          const brand = this.taxonomyService.brands.find((brand) => {
            return brand.id === brandId;
          });
          if (brand) {
            brands.push(brand.title);
          }
        }
      }
    }

    this.appendCustomSkillTitles(skills);

    const locationContext = this.locationContextToSave;
    const { travelExpectations } = this.copilotActivationStore.localJobData;
    const city = this.copilotActivationStore.localJobData.city?.trim();
    const state = this.copilotActivationStore.localJobData.state?.trim();

    const options: GenerateScreenerQuestionsOptions = {
      title,
      skills,
      brands,
      locationContext,
      location: city && state ? `${city}, ${state}` : '',
      travelExpectations,
    };

    const generatedDescriptionDocumentId =
      await this.jobPersistence.generateScreenerQuestions(options);

    if (generatedDescriptionDocumentId) {
      // Start polling
      this.copilotActivationStore.generatedScreenerQuestionsDocumentId =
        generatedDescriptionDocumentId;

      this.startPollingScreenerQuestionsGeneration(generatedDescriptionDocumentId);
    } else {
      this.copilotActivationStore.generatedScreenerQuestionsDocumentId = '';
      this.copilotActivationStore.isGeneratingScreenerQuestions = false;
    }

    return generatedDescriptionDocumentId;
  }

  public clearScreenerQuestionsGenerationPolling(): void {
    if (this.copilotActivationStore.screenerQuestionsPollingIntervalId) {
      window?.clearInterval(this.copilotActivationStore.screenerQuestionsPollingIntervalId);
    }

    this.copilotActivationStore.screenerQuestionsPollingIntervalId = null;
    this.copilotActivationStore.screenerQuestionsPollingTriedAttempts = 0;
    this.copilotActivationStore.isGeneratingScreenerQuestions = false;
  }

  public async startPollingScreenerQuestionsGeneration(documentId: string): Promise<void> {
    this.copilotActivationStore.screenerQuestionsPollingIntervalId = window?.setInterval(
      async () => {
        const generatedDescriptionDocument =
          this.copilotActivationStore.generatedScreenerQuestionsDocument;

        if (generatedDescriptionDocument?.screeningQuestions.fullfilled) {
          // Description already generated, parse it and stop polling
          SnackbarService.success('Screener questions generated successfully');
          this.clearScreenerQuestionsGenerationPolling();
        } else {
          // Screener questions not generated yet, keep polling
          const pollingAttempts = this.copilotActivationStore.screenerQuestionsPollingTriedAttempts;
          const maxAttemps = this.copilotActivationStore.screenerQuestionsPollingMaxAttempts;
          if (pollingAttempts > maxAttemps) {
            this.clearScreenerQuestionsGenerationPolling();
            this.copilotActivationStore.generatedScreenerQuestionsDocumentId = '';
            SnackbarService.critical(
              'There was a problem generating the screener questions, try again later.',
            );
          } else if (this.copilotActivationStore.stopGeneratingScreenerQuestionsFlag) {
            this.clearScreenerQuestionsGenerationPolling();
            this.copilotActivationStore.generatedScreenerQuestionsDocumentId = '';
            this.copilotActivationStore.stopGeneratingScreenerQuestionsFlag = false;
            SnackbarService.caution('Screener questions generation was stopped.');
          } else {
            this.copilotActivationStore.generatedScreenerQuestionsDocument =
              await this.jobPersistence.getGeneratedScreenerQuestions(documentId);
            this.copilotActivationStore.screenerQuestionsPollingTriedAttempts += 1;
          }
        }
      },
      this.copilotActivationStore.screenerQuestionsPollingIntervalTime,
    );
  }

  public stopGeneratingScreenerQuestions(): void {
    this.copilotActivationStore.stopGeneratingScreenerQuestionsFlag = true;
  }
  // ====== END: SCREENING QUESTIONS GENERATION ======

  // ====== START: JOB UPDATE ======
  public async updateJob(scheduledUpdates: boolean = false): Promise<JobUpdateOutcome> {
    if (!this.copilotActivationStore.selectedJobId) {
      return { customSkillSaveFailed: false, customMachineSaveFailed: false };
    }

    const jobId = this.copilotActivationStore.selectedJobId;
    const projectId = this.copilotActivationStore.selectedProjectId;
    const employerId =
      this.meService.employer?.id ?? this.projectService.currentProject?.employerId;

    let usingJobTitleId = this.copilotActivationStore.localJobData.jobTitleId;
    if (this.copilotActivationStore.localJobData.displayTitle) {
      try {
        const jobTitle = await this.fetchRelatedJobTitleForDisplayTitle(
          this.copilotActivationStore.localJobData.displayTitle,
        );

        if (jobTitle) {
          usingJobTitleId = jobTitle.id;
        }
      } catch (error) {
        // Fail silently
      }
    }

    // Get skills map objects
    const knowledgeDisciplines: string[] = [];
    const machinesBrands: { title: string; brands: string[] }[] = [];

    for (const skillId of this.copilotActivationStore.localKnowledgeDisciplines) {
      const skill = this.taxonomyService.knowledgeDisciplines.find((knowledgeDiscipline) => {
        return knowledgeDiscipline.id === skillId;
      });
      if (skill) {
        knowledgeDisciplines.push(skill.title);
      }
    }

    for (const machineId of this.copilotActivationStore.localMachines) {
      const machine = this.taxonomyService.machines.find((machine) => {
        return machine.id === machineId;
      });
      if (machine) {
        const machineBrands: { title: string; brands: string[] } = {
          title: machine.title,
          brands: [],
        };

        const brandsForMachine = this.copilotActivationStore.localBrandsPerMachine[machineId];
        for (const brandId of brandsForMachine) {
          const brand = this.taxonomyService.brands.find((brand) => {
            return brand.id === brandId;
          });
          if (brand) {
            machineBrands.brands.push(brand.title);
          }
        }

        machinesBrands.push(machineBrands);
      }
    }

    for (const customKnowledgeDiscipline of this.copilotActivationStore
      .localCustomKnowledgeDisciplines) {
      this.pushUniqueSkillTitle(knowledgeDisciplines, customKnowledgeDiscipline);
    }

    for (const customMachine of this.copilotActivationStore.localCustomMachines) {
      const trimmedMachine = customMachine.trim();

      if (!trimmedMachine) {
        continue;
      }

      const hasMachine = machinesBrands.some(
        (machineBrands) =>
          machineBrands.title.trim().toLocaleLowerCase() === trimmedMachine.toLocaleLowerCase(),
      );

      if (!hasMachine) {
        machinesBrands.push({ title: customMachine, brands: [] });
      }
    }

    // Shift option "No shift specified" is set a value of -1, but is stored as null in the db
    let usingShift = this.copilotActivationStore.localJobData.shift;
    if (usingShift === -1) {
      usingShift = null;
    }

    // Since we can save a job without a title, we need to check if the title is 'No Title'
    // and set the usingJobTitleId to null
    const newTitle =
      this.copilotActivationStore.localJobData.displayTitle?.trim().toLocaleLowerCase() ===
      'no title'
        ? ''
        : this.copilotActivationStore.localJobData.displayTitle;
    if (newTitle === '') usingJobTitleId = null;

    await this.employerService.fetchEmployerPolicies();
    this.enforceEmployerPolicy();

    // A custom skill/machine failure must not block the core job update; fall back to
    // what was entered locally (not []) so it doesn't read as "removed" in the form.
    let customSkillSaveFailed = false;
    let customMachineSaveFailed = false;

    const customSkillPayload = this.buildCustomSkillPayload();
    const customSkillSave =
      projectId && employerId
        ? this.customSkillPersistence
            .replaceProjectCustomSkills(employerId, projectId, customSkillPayload)
            .catch((error: unknown) => {
              ErrorService.captureException(error);
              customSkillSaveFailed = true;
              return customSkillPayload;
            })
        : Promise.resolve([] as JobCustomSkill[]);

    const customMachinePayload = this.buildCustomMachinePayload();
    const customMachineSave =
      projectId && employerId
        ? this.customMachinePersistence
            .replaceProjectCustomMachines(employerId, projectId, customMachinePayload)
            .catch((error: unknown) => {
              ErrorService.captureException(error);
              customMachineSaveFailed = true;
              return customMachinePayload;
            })
        : Promise.resolve([] as JobCustomMachine[]);

    const [savedCustomSkills, savedCustomMachines] = await Promise.all([
      customSkillSave,
      customMachineSave,
      this.jobPersistence.updateJob(jobId, {
        scheduledUpdates,
        activeOutreachEnabled: this.copilotActivationStore.activeOutreachEnabled,
        knowledgeDisciplines: this.copilotActivationStore.localKnowledgeDisciplines,
        machineBrands: this.copilotActivationStore.localBrandsPerMachine,
        candidateScreening: {
          screeningAction: this.copilotActivationStore.localScreeningAction,
          employerCareerSiteUrl: this.copilotActivationStore.localEmployerCareerSiteUrl,
          screeningType: this.copilotActivationStore.localScreeningType,
        },
        screenerQuestions: this.copilotActivationStore.localScreenerQuestions.map(
          (question) => question.text,
        ),
        jobDetails: {
          jobTitleId: usingJobTitleId,
          street1: this.copilotActivationStore.localJobData.street1,
          city: this.copilotActivationStore.localJobData.city,
          state: this.copilotActivationStore.localJobData.state,
          postalCode: this.copilotActivationStore.localJobData.postalCode,
          locationContext: this.locationContextToSave,
          travelExpectations: this.copilotActivationStore.localJobData.travelExpectations,
          displayTitle: newTitle,
          hireType: this.copilotActivationStore.localJobData.hireType,
          payRateMin: this.copilotActivationStore.localJobData.payRateMin,
          payRateMax: this.copilotActivationStore.localJobData.payRateMax,
          payRateSalaryMin: this.copilotActivationStore.localJobData.payRateSalaryMin,
          payRateSalaryMax: this.copilotActivationStore.localJobData.payRateSalaryMax,
          payRatePeriod: this.copilotActivationStore.localJobData.payRatePeriod,
          shift: usingShift,
          startTime: this.copilotActivationStore.localJobData.startTime,
          endTime: this.copilotActivationStore.localJobData.endTime,
          anonymous: this.copilotActivationStore.localJobData.anonymous,
          description: this.copilotActivationStore.localJobData.description,
          rawDescription: this.copilotActivationStore.localJobData.rawDescription,
          knowledgeDisciplines,
          machinesBrands,
        },
      }),
    ]);

    if (projectId && employerId) {
      this.tempCustomMachines = savedCustomMachines.map((m) => m.title);
      this.mapCustomSkillData(savedCustomSkills);
    }

    return { customSkillSaveFailed, customMachineSaveFailed };
  }

  // ====== END: JOB UPDATE ======

  public enforceEmployerPolicy(): void {
    const copilotOutreachPolicy = this.employerService.getPolicyByName(PolicyName.COPILOT_OUTREACH);
    const scheduledUpdatesPolicy = this.employerService.getPolicyByName(
      PolicyName.SCHEDULED_UPDATES,
    );
    const interviewSchedulingPolicy = this.employerService.getPolicyByName(
      PolicyName.INTERVIEW_SCHEDULING,
    );
    const dynamicScreeningPolicy = this.employerService.getPolicyByName(
      PolicyName.DYNAMIC_SCREENING,
    );
    const staticScreeningPolicy = this.employerService.getPolicyByName(PolicyName.STATIC_SCREENING);

    if (this.isNotOptionalEmployerPolicy(copilotOutreachPolicy)) {
      this.activeOutreachEnabled = copilotOutreachPolicy === PolicyEnforcement.REQUIRED;
    }

    if (this.isNotOptionalEmployerPolicy(scheduledUpdatesPolicy)) {
      this.scheduledUpdates = scheduledUpdatesPolicy === PolicyEnforcement.REQUIRED;
    }

    if (this.isNotOptionalEmployerPolicy(interviewSchedulingPolicy)) {
      this.interviewSettingsEnabled = interviewSchedulingPolicy === PolicyEnforcement.REQUIRED;
    }

    if (dynamicScreeningPolicy && staticScreeningPolicy) {
      if (dynamicScreeningPolicy === PolicyEnforcement.REQUIRED) {
        this.screeningType = ScreeningType.DYNAMIC;
        return;
      }

      if (staticScreeningPolicy === PolicyEnforcement.REQUIRED) {
        this.screeningType = ScreeningType.STATIC;
        return;
      }

      if (
        dynamicScreeningPolicy === PolicyEnforcement.FORBIDDEN &&
        staticScreeningPolicy === PolicyEnforcement.FORBIDDEN
      ) {
        this.screeningType = 'SKIP';
        return;
      }
    }

    // Default to DYNAMIC screening when user has choice and no screening type is set
    if (!this.screeningType) {
      const isUserChoice =
        (!dynamicScreeningPolicy || dynamicScreeningPolicy === PolicyEnforcement.OPTIONAL) &&
        (!staticScreeningPolicy || staticScreeningPolicy === PolicyEnforcement.OPTIONAL);

      if (isUserChoice) {
        this.screeningType = ScreeningType.DYNAMIC;
      }
    }
  }

  private isNotOptionalEmployerPolicy(enforcement: PolicyEnforcement | undefined): boolean {
    return enforcement &&
      [PolicyEnforcement.REQUIRED, PolicyEnforcement.FORBIDDEN].includes(enforcement)
      ? true
      : false;
  }
}
